import { validBirthDate, birthDateFromBR } from '../../helpers/ClientBirthDate';
import { useRef, useState } from 'react';
import { ProposalService } from '../../services';
import Modal from '../Modal';
import LoadingSpinner from '../LoadingSpinner/LoadingSpinner';
import HelperDate from '../../helpers/HelperDate';
import Mask from '../../contants/Mask';
import HelperFile from '../../helpers/helperFile';
import HelperNumber from '../../helpers/HelperNumber';


interface IProp {
  proposal: any;
}
const AdditionalInformationComun = ({ proposal }: IProp) => {
  const [checkFilesStatus, setCheckFilesStatus] = useState<boolean>(true);
  const [isLoad, setIsLoad] = useState<boolean>(false);
  const [uploadedDocs, setUploadedDocs] = useState<Record<string, Array<{ fileName: string; urlBlob: string; keyType: string; fileType: number; documentType: number }>>>({});
  const [documentErrors, setDocumentErrors] = useState<Record<string, string>>({});

  const isCpfPerson = () => {
    const document = HelperNumber.justNumber(proposal?.client?.document ?? '');
    return document.length <= 11;
  };

  const isCnpjPerson = () => !isCpfPerson();

  const getDocumentConfig = () => {
    if (isCnpjPerson()) {
      return [
        { key: 'contratoSocial', label: 'Contrato social ou última alteração contratual', keyType: 'CONTRATO_SOCIAL', documentType: 19, required: true },
        { key: 'comprovanteEnderecoEmpresa', label: 'Comprovante de endereço da empresa', keyType: 'COMPROVANTE_ENDERECO_EMPRESA', documentType: 20, required: true },
        { key: 'cpfRepresentante', label: 'CPF do representante', keyType: 'CPF_REPRESENTANTE', documentType: 41, required: true },
        { key: 'rgRepresentante', label: 'RG do representante', keyType: 'RG_REPRESENTANTE', documentType: 42, required: true },
        { key: 'cnhRepresentante', label: 'CNH do representante', keyType: 'CNH_REPRESENTANTE', documentType: 43, required: true }
      ];
    }

    return [
      { key: 'cpf', label: 'CPF', keyType: 'CPF', documentType: 38, required: true },
      { key: 'rg', label: 'RG', keyType: 'RG', documentType: 39, required: true },
      { key: 'cnh', label: 'CNH', keyType: 'CNH', documentType: 40, required: false },
      { key: 'certidaoCasamento', label: 'Certidão de Casamento', keyType: 'CERTIDAO_CASAMENTO', documentType: 13, required: proposal.client?.maritalStatus === 3 },
      { key: 'comprovanteResidencia', label: 'Comprovante de residência', keyType: 'COMPROVANTE_RESIDENCIA', documentType: 14, required: true }
    ];
  };

  const hasUploadedFiles = (key: string) => (uploadedDocs[key] ?? []).length > 0;

  const validateDocuments = (
  docs: typeof uploadedDocs
): Record<string, string> => {
  const errors: Record<string, string> = {};

  const hasFile = (key: string) => (docs[key] ?? []).length > 0;

  const validateIdentity = (
    cpfKey: string,
    rgKey: string,
    cnhKey: string
  ) => {
    const hasCpf = hasFile(cpfKey);
    const hasRg = hasFile(rgKey);
    const hasCnh = hasFile(cnhKey);

    if (!hasCnh && !(hasCpf && hasRg)) {
      if (!hasCpf && !hasRg) {
        errors[cnhKey] = 'Anexe a CNH ou o CPF e RG.';
      } else if (!hasCpf) {
        errors[cpfKey] = 'Anexe o CPF ou a CNH.';
      } else {
        errors[rgKey] = 'Anexe o RG ou a CNH.';
      }
    }
  };

  if (isCpfPerson()) {
    validateIdentity('cpf', 'rg', 'cnh');

    if (!hasFile('comprovanteResidencia')) {
      errors.comprovanteResidencia =
        'O comprovante de residência é obrigatório.';
    }

    if (
      proposal.client?.maritalStatus === 3 &&
      !hasFile('certidaoCasamento')
    ) {
      errors.certidaoCasamento =
        'A certidão de casamento é obrigatória.';
    }
  } else {
    validateIdentity(
      'cpfRepresentante',
      'rgRepresentante',
      'cnhRepresentante'
    );

    if (!hasFile('contratoSocial')) {
      errors.contratoSocial =
        'O contrato social é obrigatório.';
    }

    if (!hasFile('comprovanteEnderecoEmpresa')) {
      errors.comprovanteEnderecoEmpresa =
        'O comprovante de endereço da empresa é obrigatório.';
    }
  }

  if (
    proposal?.proposalType?.name === 'Solar Direct' &&
    !hasFile('propriedadeImovel')
  ) {
    errors.propriedadeImovel =
      'O documento de propriedade do imóvel é obrigatório.';
  }

  return errors;
};

function checkFile() {
  const errors = validateDocuments(uploadedDocs);

  setDocumentErrors(errors);

  const hasError = Object.keys(errors).length > 0;
  setCheckFilesStatus(hasError);

  return hasError;
}

  const handleUploadDocument = async (event: React.ChangeEvent<HTMLInputElement>, key: string, documentType: number, keyType: string) => {
    const files = Array.from(event.target.files ?? []);

    if (!files.length) {
      return;
    }

    try {
      const urls = await HelperFile.uploadFiles(event.target, 'proposal/DocumentClient');

      const items = urls.map((urlBlob, index) => ({
        fileName: files[index]?.name ?? `${key}-${index + 1}`,
        urlBlob,
        keyType,
        fileType: proposal.client.userType,
        documentType
      }));

      const updatedDocs = {
        ...uploadedDocs,
        [key]: items
      };

      setUploadedDocs(updatedDocs);

      const errors = validateDocuments(updatedDocs);

      setDocumentErrors(errors);
      setCheckFilesStatus(Object.keys(errors).length > 0);

      return updatedDocs;
    } catch (error: any) {
      setUploadedDocs(prev => ({
        ...prev,
        [key]: []
      }));

      Modal.show({
        title: 'Erro ao anexar documento',
        body: Modal.getErrorMessage(error, { key, documentType }),
        showCancel: true,
        showConfirm: false,
        onCancelText: 'Fechar'
      });
    }
  };

  const submitting = useRef(false);
  async function ProposalToAcceptAndFiles() {
  if (submitting.current) return;

  if (checkFile()) {
    return;
  }

  const files = Object.values(uploadedDocs).flat();

  if (files.length === 0) {
    checkFile();
    return;
  }

  submitting.current = true;
  setIsLoad(true);

  const payload = {
    proposalId: proposal.id,
    filesInBase64: files
  };

  try {

    await ProposalService.SaveAdditionalFilesRecharge(payload);

    Modal.show({
      title: 'Sucesso',
      body: (
        <>
          <p>Salvo com sucesso!</p>
        </>
      ),
      showCancel: false,
      showConfirm: true
    });

    window.location.reload();
  } catch (error: any) {
    submitting.current = false;
    setIsLoad(false);

    Modal.show({
      title: 'Erro ao salvar',
      body: Modal.getErrorMessage(error, payload),
      showCancel: true,
      showConfirm: false,
      onCancelText: 'Fechar'
    });
  }
}

  return (
    <>
      {
        isLoad &&
        <LoadingSpinner text='Processando Arquivo...' />
      }

      {getDocumentConfig().map((document) => {
        if (document.key === 'cnh' && !isCpfPerson()) {
          return null;
        }

        const identityKeys = isCpfPerson()
        ? ['cpf', 'rg', 'cnh']
        : ['cpfRepresentante', 'rgRepresentante', 'cnhRepresentante'];

      const isIdentityDocument = identityKeys.includes(document.key);

      const isRequired = isIdentityDocument
        ? !(
            hasUploadedFiles(isCpfPerson() ? 'cnh' : 'cnhRepresentante') ||
            (
              hasUploadedFiles(isCpfPerson() ? 'cpf' : 'cpfRepresentante') &&
              hasUploadedFiles(isCpfPerson() ? 'rg' : 'rgRepresentante')
            )
          )
        : document.required;
        return (
          <div key={document.key} className="form-group" style={{ marginBottom: 10 }}>
            <label htmlFor={`document-${document.key}`}>
              {document.label}{isRequired ? ' *' : ''}
            </label>
            <input
              type="file"
              id={`document-${document.key}`}
              name={document.key}
              onChange={(event) => handleUploadDocument(event, document.key, document.documentType, document.keyType)}
              accept=".pdf,.jpg,.jpeg,.png"
              className={'form-control'}
              multiple
            />
            {documentErrors[document.key] && (
              <span
                className="error-message"
                style={{
                  color: '#f52020',
                  fontSize: 10,
                  display: 'block',
                  marginTop: 4
                }}
              >
                {documentErrors[document.key]}
              </span>
            )}
          </div>
        );
      })}

      {
         ((proposal?.proposalType?.name ?? '') === 'Solar Direct') &&
          <div className="form-group" style={{ marginBottom: 10 }}>
            <label htmlFor="documentos" style={{marginBottom:2}}> Propriedade do imóvel (*)</label>
            <span style={{fontSize:12, color:'#9b9898', display:'block', fontWeight:'normal'}}>(tipo: IPTU)</span>
            <input
              type="file"
              id="documentosImoveisAll"
              name="documentosImoveisAll"
              onChange={(event) => handleUploadDocument(event, 'propriedadeImovel', 26, 'IMOVEL')}
              accept=".pdf,.jpg,.jpeg,.png"
              className={'form-control'}
              multiple
            />
            {documentErrors.propriedadeImovel && (
              <span className="error-message" style={{ color: '#f52020', fontSize: 10 }}>
                {documentErrors.propriedadeImovel}
              </span>
            )}
          </div>

      }
      <span className="d-block" style={{ color: '#615f5fff', fontSize: 11 }}>* Aceita apenas documentos dos tipos: .pdf, .jpg, .jpeg, .png com máximo de 5mb</span>
      <button type="button" disabled={isLoad} className="btn btn-success btn-lg waves-effect waves-light" onClick={ProposalToAcceptAndFiles} style={{ width: '100%', marginTop: 10 }}>
        {'Salvar Documentos'}
        </button>
    </>
  )
};

export default AdditionalInformationComun;
